import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { getAwards } from "../api/award-api";
import type { AwardsResponse } from "../types/award.type";

const STALE_TIME = 30_000;

/**
 * Shared React Query key for award data. Both `useAwards` (read) and
 * `useUpsertAwards` (write invalidation) must use this exact key so a mutation
 * on either PanelMeetingStep or FinalReportAndAwardStep refreshes the same cache.
 */
export const awardsQueryKey = (rfxId: number, evalId: number) =>
  ["rfx", rfxId, "evaluation", evalId, "awards"] as const;

interface UseAwardsResult {
  awardsData: AwardsResponse | undefined;
  isLoading: boolean;
}

export const useAwards = (
  rfxId: number | undefined,
  evalId: number | undefined
): UseAwardsResult => {
  const query: UseQueryResult<AwardsResponse> = useQuery({
    queryKey: awardsQueryKey(rfxId ?? 0, evalId ?? 0),
    queryFn: () => getAwards(rfxId!, evalId!),
    enabled: !!rfxId && !!evalId,
    staleTime: STALE_TIME,
  });

  return {
    awardsData: query.data,
    isLoading: query.isLoading,
  };
};
